import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import cx from 'helpers/cx'
import getSearchLink from 'helpers/getSearchLink'
import { getContrast } from 'polished'

import { SearchIcon } from '@pleio/icons/search'

import HideVisually from 'js/components/HideVisually/HideVisually'
import IconButton from 'js/components/IconButton'
import NavigationAction from 'js/layout/Navigation/components/NavigationAction'

import styles from './NavigationSearch.module.css'

type NavigationSearchProps = {
    hideSidebar?: () => void
}

const NavigationSearch = ({ hideSidebar }: NavigationSearchProps) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const headerMain = getComputedStyle(document.documentElement)
        .getPropertyValue('--ref-palette-header-main')
        .trim()
    const searchBarBorder =
        headerMain && getContrast(headerMain, 'white') >= 3
            ? 'transparent'
            : 'var(--ref-palette-neutral-40-alpha)'

    const [isFocused, setIsFocused] = useState(false)
    const handleFocus = () => setIsFocused(true)
    const handleBlur = () => setIsFocused(false)

    const [searchValue, setSearchValue] = useState('')
    const onChange = (evt) => setSearchValue(evt.target.value)

    const clearSearch = () => {
        setSearchValue('')
        refToggle.current.focus()
    }

    const onSubmit = () => {
        navigate(
            getSearchLink({
                q: searchValue.trim(),
            }),
        )
        clearSearch()
        if (hideSidebar) {
            hideSidebar()
        }
    }

    const onKeyDown = (evt) => {
        if (!disabled && evt.key === 'Enter') {
            onSubmit()
        }
        if (evt.key === 'Escape') {
            clearSearch()
        }
    }

    const refToggle = useRef<HTMLButtonElement>(null)
    const refInput = useRef<HTMLInputElement>(null)

    const focusInput = () => refInput.current.focus()

    const disabled = isFocused && !searchValue

    const inputIsVisible = isFocused || !!searchValue

    const wrapperStyle: React.CSSProperties & Record<string, string> = {
        '--search-bar-border': searchBarBorder,
    }

    return (
        <div
            className={cx(styles.wrapper, inputIsVisible && styles.isVisible)}
            style={wrapperStyle}
        >
            <NavigationAction
                ref={refToggle}
                tabIndex={inputIsVisible ? -1 : 0}
                tooltip={t('global.search')}
                aria-label={t('global.search')}
                onClick={focusInput}
                aria-expanded={inputIsVisible}
                aria-controls="nav-search"
            >
                <SearchIcon />
            </NavigationAction>
            <div className={styles.searchFieldWrapper}>
                <HideVisually as="label" htmlFor="nav-search">
                    {t('global.search')}
                </HideVisually>
                <input
                    ref={refInput}
                    tabIndex={inputIsVisible ? 0 : -1}
                    className={styles.searchInput}
                    name="nav-search"
                    id="nav-search"
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={onKeyDown}
                    onChange={onChange}
                    value={searchValue}
                    placeholder={t('global.search')}
                />
                <IconButton
                    className={styles.submitSearchButton}
                    tabIndex={inputIsVisible ? 0 : -1}
                    bgOffset="sm"
                    variant={disabled ? 'tertiary' : 'secondary'}
                    radiusStyle="round"
                    onClick={onSubmit}
                    disabled={disabled}
                    aria-label={t('global.search')}
                    aria-expanded={inputIsVisible}
                    aria-controls="nav-search"
                >
                    <SearchIcon />
                </IconButton>
            </div>
        </div>
    )
}

export default NavigationSearch
